import { computed, ref, onMounted, onUnmounted, Ref, onBeforeUnmount } from "vue";
import { useOnResize } from "vue-composable";

const { width } = useOnResize(document.body);
export const isMobile = computed(() => {
  return width.value < 810;
});

export function useOverflowObserver(
  target: Ref<HTMLElement | null>,
): { isOverflowing: Ref<boolean>; checkOverflow: () => void } {
  const isOverflowing = ref(false);

  const checkOverflow = () => {
    if (target?.value) {
      const el = target?.value;
      isOverflowing.value = el.scrollHeight > el.clientHeight;
    }
  };

  const resizeListener = () => {
    checkOverflow();
  };

  onMounted(() => {
    checkOverflow();
    window.addEventListener("resize", resizeListener);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", resizeListener);
  });

  return { isOverflowing, checkOverflow };
}

export function useResizeAndContentObserver(
  el: Ref<HTMLElement | null>,
  cb: () => void,
  opts: { debounce?: number; box?: ResizeObserverBoxOptions } = {},
) {
  let ro: ResizeObserver | null = null;
  let mo: MutationObserver | null = null;
  let t: number | null = null;

  const fire = () => {
    if (opts.debounce && opts.debounce > 0) {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(cb, opts.debounce);
    } else {
      cb();
    }
  };

  onMounted(() => {
    const elv = el.value;
    if (!elv) return;

    ro = new ResizeObserver(() => fire());

    ro.observe(elv, { box: opts.box ?? "border-box" });

    mo = new MutationObserver(() => fire());
    mo.observe(elv, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: false,
    });

    // initial call so you can compute on mount
    queueMicrotask(fire);
  });

  onBeforeUnmount(() => {
    ro?.disconnect();
    mo?.disconnect();
    if (t) window.clearTimeout(t);
  });
}
